<template>
  <div class="modal-card" style="height: 100%;">
    <header class="modal-card-head">
      <p class="modal-card-title">Project: {{ selectedProjectId }}</p>
    </header>
    <section class="modal-card-body">
      <b-table
        :data="tableData"
        :loading="loading"
        :selected.sync="selected"
        focusable
      >
        <template slot-scope="props">
          <b-table-column field="name" label="Name">
            {{ props.row.name }}
          </b-table-column>
          <b-table-column field="state" label="State" width="3rem">
            <span v-if="props.row.state === 'empty'" class="tag">
              Empty
            </span>
            <span
              v-else-if="props.row.state === 'finished'"
              class="tag is-success"
            >
              Finished
            </span>
            <span v-else-if="props.row.state === 'wip'" class="tag is-warning">
              WIP
            </span>
          </b-table-column>
        </template>
      </b-table>
      <b-pagination
        :per-page="perPage"
        :total="total"
        :current.sync="currentPage"
        @change="onPageChange"
      >
        <b-pagination-button
          slot="previous"
          slot-scope="props"
          :page="props.page"
        >
          Previous
        </b-pagination-button>

        <b-pagination-button slot="next" slot-scope="props" :page="props.page">
          Next
        </b-pagination-button>
      </b-pagination>
    </section>
    <footer class="modal-card-foot">
      <b-button @click="$parent.close()">Close</b-button>
      <b-button type="is-info" :disabled="disabled" @click="onClickSelect"
        >Select</b-button
      >
      <div class="spacer"></div>
      <modal-button
        label="Switch Project"
        :icon="mdiFolderMultipleOutline"
        :modal-setting="{
          hasModalCard: true,
          trapFocus: true
        }"
      >
        <project-selector :on-project-switch="onProjectSwitch" />
      </modal-button>
    </footer>
  </div>
</template>

<script>
import isEmpty from "lodash/isEmpty";
import { computed, ref, onMounted } from "@vue/composition-api";
import { SnackbarProgrammatic as Snackbar } from "buefy";
import useWarnings from "@/composables/useWarnings";
import ui from "@/store/modules/ui";
import $axios from "../axios-instance";
import ModalButton from "@/components/ModalButton";
import ProjectSelector from "@/components/ProjectSelector";
import { mdiFolderMultipleOutline } from "@mdi/js";

export default {
  components: { ModalButton, ProjectSelector },
  setup(_props, { parent }) {
    // Table related variables
    const loading = ref(false);
    const perPage = 20;
    const total = ref(0);
    const currentPage = ref(1);
    const pagingCounter = ref(1);
    const selected = ref({});
    const disabled = computed(() => {
      if (isEmpty(selected.value)) {
        return true;
      } else {
        return false;
      }
    });
    // data to feed in to table
    const floorplans = computed(() => ui.currentProject.floorplans);

    const tableData = computed(() => {
      // error handling
      if (floorplans.value.length === 0) {
        return [];
      }

      return floorplans.value.map(floorplan => {
        return {
          name: floorplan.name,
          state: floorplan.status
        };
      });
    });

    // events
    const { shouldProceed } = useWarnings();
    const onClickSelect = async () => {
      // Find the page number of the selected row
      const index = tableData.value.findIndex(
        o => o.name === selected.value.name
      );
      // If you select and move the page, you can't find the
      // selected row in the current table
      if (index < 0) {
        selected.value = {};
        Snackbar.open({
          message: "Couldn't fint selected in current page",
          actionText: "Close",
          type: "is-warning",
          indefinite: true,
          position: "is-top"
        });
        return;
      }
      const nextPage = pagingCounter.value + index;
      // check for unsaved changes
      if (!shouldProceed()) return;
      parent.close();
      // start loading
      ui.setIsLoadingAnnotation(true);
      // sync floorplan
      try {
        await ui.syncSelectedImage(selected.value.name);
        await ui.syncSelectedFloorplanPage(nextPage);
        ui.setHasUnsavedChange(false);
      } catch (error) {
        console.error("Error at onClickSelect at FloorplanBrowser: " + error);
        Snackbar.open({
          message: "Accessing to server failed.",
          actionText: "Close",
          type: "is-warning",
          indefinite: true,
          position: "is-top"
        });
      } finally {
        ui.setIsLoadingAnnotation(false);
      }
    };
    const onPageChange = async page => {
      currentPage.value = page;
      loading.value = true;
      try {
        loadAsyncData();
      } finally {
        loading.value = false;
      }
    };
    const onProjectSwitch = async () => {
      try {
        await loadAsyncData();
      } catch (err) {
        console.error(
          "onProjectSwitch in FloorplanBrowser failed at loadAsyncData"
        );
      }
    };
    const loadAsyncData = async () => {
      if (!ui.selectedProjectId) {
        throw new Error("Project not selected");
      }
      const params = [`page=${currentPage.value}`, `limit=${perPage}`].join(
        "&"
      );
      const res = await $axios.get(
        `/users/${ui.currentUserId}/project/${ui.selectedProjectId}?${params}`
      );
      const data = res.data;
      if (!data || !data.success) {
        throw new Error("Serve Access Failed");
      }
      // Place fetched floorplan infos in global state
      // TODO: maybe its possible to keep it local ?
      ui.setProjectFloorplans(data.payload.docs);
      // Buefy Table requires these to make itself work
      total.value = data.payload.totalDocs;
      currentPage.value = data.payload.currentPage;
      pagingCounter.value = data.payload.pagingCounter;
    };

    // projects list
    onMounted(async () => {
      // fetch current project data
      loading.value = true;
      try {
        await loadAsyncData();
      } catch (error) {
        console.error(
          "mounted() in FloorplanBrowser failed at loadAsyncData: " + error
        );
      } finally {
        loading.value = false;
      }
    });

    return {
      loading,
      tableData,
      selected,
      currentPage,
      disabled,
      onClickSelect,
      selectedProjectId: computed(() => ui.selectedProjectId),
      mdiFolderMultipleOutline,
      perPage,
      total,
      onPageChange,
      onProjectSwitch
    };
  }
};
</script>

<style lang="scss" scoped>
.spacer {
  flex: 1 1 0;
}
</style>
